// ReportDayOfTheWeek.jsx
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import React, { useMemo } from "react";
import Colours from "../../../helpers/colours";
import NoDataRow from "../../NoDataRow";
import HelperRatePlanName from "./HelperRatePlanName";
import InfoIcon from "../../InfoIcon";

const weekDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

const METRICS = {
    "Total Price": "revenueTotal",
    "Booking Total": "bookingsTotal",
    "Room Nights": "roomNightsTotal",
};

export const REPORT_DAY_OF_THE_WEEK_TABLE = "REPORT_DAY_OF_THE_WEEK_TABLE";
const ReportDayOfTheWeekTable = ({
    data,
    js,
    title,
    description,
    metric,
    showDate = false,
}) => {
    const theme = useTheme();
    const tableOverflow = useMediaQuery(theme.breakpoints.down("md"));

    const selectedMetrics = useMemo(() => {
        const metrics = Array.isArray(metric) ? metric : [metric];
        return metrics.filter(Boolean);
    }, [metric]);

    const isMultiMetric = selectedMetrics.length > 1;

    const renameInfoByRate = useMemo(() => {
        return Object.values(data || {}).reduce((acc, item) => {
            const main = String(item.offerRenameInfo?.main ?? "").trim();

            if (main && item.offerRenameInfo?.names?.length > 1) {
                acc[main] = item.offerRenameInfo;
            }

            return acc;
        }, {});
    }, [data]);

    const groupedByWeekdayAndRate = useMemo(
        () =>
            Object.values(data || {}).reduce((acc, item) => {
                const keyMap = Object.fromEntries(item.keys || []);
                const rate = keyMap.OfferTitle;

                const day = new Date(
                    Date.UTC(
                        Number(keyMap.Year),
                        Number(keyMap.Month) - 1,
                        Number(keyMap.Day),
                    ),
                ).toLocaleDateString("en-US", {
                    weekday: "long",
                    timeZone: "UTC",
                });

                acc[day] ??= {};
                acc[day][rate] ??= {
                    revenueTotal: 0,
                    revenueRoom: 0,
                    bookingsTotal: 0,
                    bookingsRooms: 0,
                    roomNightsTotal: 0,
                };

                acc[day][rate].revenueTotal += item.result?.revenueTotal || 0;
                acc[day][rate].revenueRoom += item.result?.revenueRoom || 0;
                acc[day][rate].bookingsTotal += item.result?.bookingsTotal || 0;
                acc[day][rate].bookingsRooms += item.result?.bookingsRooms || 0;
                acc[day][rate].roomNightsTotal +=
                    item.result?.roomNightsTotal || 0;

                return acc;
            }, {}),
        [data],
    );

    const datesByWeekday = useMemo(() => {
        return Object.values(data || {}).reduce((acc, item) => {
            const keyMap = Object.fromEntries(item.keys || []);

            const date = new Date(
                Date.UTC(
                    Number(keyMap.Year),
                    Number(keyMap.Month) - 1,
                    Number(keyMap.Day),
                ),
            );

            const day = date.toLocaleDateString("en-US", {
                weekday: "long",
                timeZone: "UTC",
            });

            const isoDate = date.toISOString().split("T")[0];

            acc[day] ??= [];
            if (!acc[day].includes(isoDate)) {
                acc[day].push(isoDate);
            }

            return acc;
        }, {});
    }, [data]);

    const formatHeaderDate = (isoDate, withYear = false) => {
        const date = new Date(`${isoDate}T00:00:00Z`);
        const day = String(date.getUTCDate()).padStart(2, "0");
        const month = String(date.getUTCMonth() + 1).padStart(2, "0");
        const year = date.getUTCFullYear();

        return withYear ? `${day}/${month}/${year}` : `${day}/${month}`;
    };

    const getHeaderInfo = (day) => {
        const dates = [...(datesByWeekday[day] || [])].sort();

        if (!showDate || dates.length === 0) {
            return {
                label: day,
                tooltip: "",
            };
        }

        if (dates.length === 1) {
            return {
                label: `${day} ${formatHeaderDate(dates[0])}`,
                tooltip: formatHeaderDate(dates[0], true),
            };
        }

        return {
            label: `${day} ${formatHeaderDate(dates[0])} - ${formatHeaderDate(
                dates[dates.length - 1],
            )}`,
            tooltip: dates
                .map((date) => formatHeaderDate(date, true))
                .join(", "),
        };
    };

    const rates = Array.from(
        new Set(
            weekDays.flatMap((day) =>
                Object.keys(groupedByWeekdayAndRate?.[day] || {}),
            ),
        ),
    ).filter((rate) =>
        selectedMetrics.some((metricName) => {
            const metricKey = METRICS[metricName];

            return weekDays.some(
                (day) =>
                    Number(
                        groupedByWeekdayAndRate?.[day]?.[rate]?.[metricKey] ||
                            0,
                    ) > 0,
            );
        }),
    );

    const headerStyles = {
        fontSize: "0.8em",
        backgroundColor: Colours.pillarBackgroundAttract,
        textAlign: "left",
        border: "1px solid lightgrey",
        fontWeight: "bold",
    };

    const formatValue = (value) => {
        return js?.numberWithCommas ? js.numberWithCommas(value) : value;
    };

    const getMetricValue = (rate, day, metricName) => {
        const metricKey = METRICS[metricName];
        return groupedByWeekdayAndRate?.[day]?.[rate]?.[metricKey] || 0;
    };

    const getStripedCellStyle = (subRowIndex, extraStyles = {}) => ({
        border: "1px solid lightgrey",
        ...(subRowIndex % 2 === 0 && {
            backgroundColor: "rgba(0, 0, 0, 0.03)",
        }),
        ...extraStyles,
    });

    return (
        <>
            <InfoIcon title={title} description={description} />

            <TableContainer
                sx={{
                    overflow: "auto",
                    maxHeight: "800px",
                }}
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    ...headerStyles,
                                    ...(tableOverflow && {
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 3,
                                    }),
                                }}
                            >
                                Rate
                            </TableCell>

                            {isMultiMetric && (
                                <TableCell
                                    sx={{
                                        ...headerStyles,
                                    }}
                                >
                                    Metric
                                </TableCell>
                            )}

                            {weekDays.map((day) => {
                                const headerInfo = getHeaderInfo(day);

                                return (
                                    <TableCell
                                        key={day}
                                        sx={{
                                            ...headerStyles,
                                            textAlign: "right",
                                        }}
                                    >
                                        {showDate && headerInfo.tooltip ? (
                                            <Tooltip
                                                title={headerInfo.tooltip}
                                                arrow
                                            >
                                                <span>{headerInfo.label}</span>
                                            </Tooltip>
                                        ) : (
                                            headerInfo.label
                                        )}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {rates.length === 0 && (
                            <NoDataRow text="No data for these filters" />
                        )}

                        {rates.map((rate, rateIndex) => {
                            if (!isMultiMetric) {
                                const metricName = selectedMetrics[0];

                                return (
                                    <TableRow key={rate}>
                                        <TableCell
                                            sx={{
                                                fontWeight: "bold",
                                                border: "1px solid lightgrey",
                                                ...(tableOverflow && {
                                                    position: "sticky",
                                                    left: 0,
                                                    zIndex: 1,
                                                    backgroundColor:
                                                        Colours.plainWhite,
                                                }),
                                            }}
                                        >
                                            <HelperRatePlanName
                                                label={rate}
                                                renameInfo={
                                                    renameInfoByRate[rate]
                                                }
                                            />
                                        </TableCell>

                                        {weekDays.map((day) => (
                                            <TableCell
                                                key={`${rate}-${day}`}
                                                sx={getStripedCellStyle(
                                                    rateIndex,
                                                    { textAlign: "right" },
                                                )}
                                            >
                                                {formatValue(
                                                    getMetricValue(
                                                        rate,
                                                        day,
                                                        metricName,
                                                    ),
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            }

                            return (
                                <React.Fragment key={rate}>
                                    {selectedMetrics.map(
                                        (metricName, metricIndex) => {
                                            const subRowIndex =
                                                rateIndex *
                                                    selectedMetrics.length +
                                                metricIndex;

                                            return (
                                                <TableRow
                                                    key={`${rate}-${metricName}`}
                                                >
                                                    {metricIndex === 0 && (
                                                        <TableCell
                                                            rowSpan={
                                                                selectedMetrics.length
                                                            }
                                                            sx={{
                                                                fontWeight:
                                                                    "bold",
                                                                border: "1px solid lightgrey",
                                                                verticalAlign:
                                                                    "top",
                                                                ...(tableOverflow && {
                                                                    position:
                                                                        "sticky",
                                                                    left: 0,
                                                                    zIndex: 1,
                                                                    backgroundColor:
                                                                        Colours.plainWhite,
                                                                }),
                                                            }}
                                                        >
                                                            <HelperRatePlanName
                                                                label={rate}
                                                                renameInfo={
                                                                    renameInfoByRate[
                                                                        rate
                                                                    ]
                                                                }
                                                            />
                                                        </TableCell>
                                                    )}

                                                    <TableCell
                                                        sx={getStripedCellStyle(
                                                            subRowIndex,
                                                        )}
                                                    >
                                                        {metricName}
                                                    </TableCell>

                                                    {weekDays.map((day) => (
                                                        <TableCell
                                                            key={`${rate}-${metricName}-${day}`}
                                                            sx={getStripedCellStyle(
                                                                subRowIndex,
                                                                {
                                                                    textAlign:
                                                                        "right",
                                                                },
                                                            )}
                                                        >
                                                            {formatValue(
                                                                getMetricValue(
                                                                    rate,
                                                                    day,
                                                                    metricName,
                                                                ),
                                                            )}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            );
                                        },
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

export default ReportDayOfTheWeekTable;
